import * as React from 'react';
import { Store, Package, Check, ChevronRight } from 'lucide-react';

import { Label } from '@/components/atoms/label';
import { Button } from '@/components/atoms/button';
import { cn } from '@/lib/utils';
import type { BannerAction, SelectedProduct } from '../../stores/campaign-wizard.store';
import { ProductPickerDialog } from '../product-picker-dialog';

// ============ Types ============

interface BannerActionSelectorProps {
  value: BannerAction;
  onChange: (action: BannerAction) => void;
  shopName?: string;
  products?: SelectedProduct[];
  className?: string;
  disabled?: boolean;
}

// ============ Component ============

export function BannerActionSelector({
  value,
  onChange,
  shopName = 'Ma boutique',
  products = [],
  className,
  disabled = false,
}: BannerActionSelectorProps) {
  const [isPickerOpen, setIsPickerOpen] = React.useState(false);

  // Get selected product info
  const selectedProduct = React.useMemo(() => {
    if (value.type === 'product' && value.productRefId) {
      return products.find((p) => p.refId === value.productRefId);
    }
    return null;
  }, [value, products]);

  // Handle action type change
  const handleSelectShop = React.useCallback(() => {
    if (disabled) return;
    onChange({ type: 'shop' });
  }, [onChange, disabled]);

  const handleSelectProduct = React.useCallback(() => {
    if (disabled) return;
    // Open picker to select a product
    setIsPickerOpen(true);
  }, [disabled]);

  // Handle product selection from picker
  const handleProductSelect = React.useCallback(
    (product: SelectedProduct) => {
      onChange({ type: 'product', productRefId: product.refId });
      setIsPickerOpen(false);
    },
    [onChange]
  );

  return (
    <div className={cn('space-y-3', className)}>
      <Label>Action au clic</Label>
      <p className="text-sm text-muted-foreground">
        Que se passe-t-il quand un utilisateur clique sur votre bannière ?
      </p>

      <div className="grid gap-3">
        {/* Shop option */}
        <button
          type="button"
          onClick={handleSelectShop}
          disabled={disabled}
          className={cn(
            'relative flex items-center gap-4 p-4 rounded-xl border-2 transition-all text-left w-full',
            value.type === 'shop'
              ? 'border-primary bg-primary/5 ring-2 ring-primary/20'
              : 'border-border hover:border-primary/50 hover:bg-muted/50',
            disabled && 'opacity-50 cursor-not-allowed'
          )}
        >
          {value.type === 'shop' && (
            <div className="absolute top-3 right-3">
              <Check className="size-5 text-primary" />
            </div>
          )}

          <div
            className={cn(
              'size-10 rounded-lg flex items-center justify-center shrink-0',
              value.type === 'shop'
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted'
            )}
          >
            <Store className="size-5" />
          </div>

          <div className="flex-1 min-w-0">
            <p className="font-medium">Rediriger vers ma boutique</p>
            <p className="text-sm text-muted-foreground truncate">
              {shopName}
            </p>
          </div>
        </button>

        {/* Product option */}
        <button
          type="button"
          onClick={handleSelectProduct}
          disabled={disabled}
          className={cn(
            'relative flex items-center gap-4 p-4 rounded-xl border-2 transition-all text-left w-full',
            value.type === 'product'
              ? 'border-primary bg-primary/5 ring-2 ring-primary/20'
              : 'border-border hover:border-primary/50 hover:bg-muted/50',
            disabled && 'opacity-50 cursor-not-allowed'
          )}
        >
          {value.type === 'product' && (
            <div className="absolute top-3 right-3">
              <Check className="size-5 text-primary" />
            </div>
          )}

          <div
            className={cn(
              'size-10 rounded-lg flex items-center justify-center shrink-0',
              value.type === 'product'
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted'
            )}
          >
            <Package className="size-5" />
          </div>

          <div className="flex-1 min-w-0">
            <p className="font-medium">Rediriger vers un produit</p>
            {selectedProduct ? (
              <div className="flex items-center gap-2 mt-1">
                {selectedProduct.image && (
                  <img
                    src={selectedProduct.image}
                    alt={selectedProduct.name}
                    className="size-6 rounded object-cover"
                  />
                )}
                <p className="text-sm text-muted-foreground truncate">
                  {selectedProduct.name}
                </p>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                Sélectionner un produit...
              </p>
            )}
          </div>

          <ChevronRight className="size-5 text-muted-foreground shrink-0" />
        </button>
      </div>

      {/* Selected product display for product action */}
      {value.type === 'product' && selectedProduct && (
        <div className="flex items-center justify-between p-3 rounded-lg border bg-muted/30">
          <div className="flex items-center gap-3 min-w-0">
            {selectedProduct.image ? (
              <img
                src={selectedProduct.image}
                alt={selectedProduct.name}
                className="size-10 rounded object-cover shrink-0"
              />
            ) : (
              <div className="size-10 rounded bg-muted flex items-center justify-center shrink-0">
                <Package className="size-5 text-muted-foreground" />
              </div>
            )}
            <div className="min-w-0">
              <p className="font-medium truncate">{selectedProduct.name}</p>
              <p className="text-xs text-muted-foreground">
                L'utilisateur sera redirigé vers ce produit
              </p>
            </div>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setIsPickerOpen(true)}
            disabled={disabled}
          >
            Changer
          </Button>
        </div>
      )}

      {/* Product picker dialog */}
      <ProductPickerDialog
        open={isPickerOpen}
        onOpenChange={setIsPickerOpen}
        onSelect={handleProductSelect}
        multiSelect={false}
        selectedProductIds={
          selectedProduct ? [selectedProduct.refId] : []
        }
      />
    </div>
  );
}
